import { type OcrWarning, OcrWarningContent } from '@/components/Pages/spots/components/dialogs/OcrWarningContent'
import { Button } from '@/components/UI/Button'
import { Checkbox } from '@/components/UI/Checkbox'
import { Dialog } from '@/components/UI/Dialog'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** ダイアログが開いているか */
  opened: boolean
  /** API から取得した OCR 注意喚起データ */
  ocrWarning?: OcrWarning | null
  /**
   * 「次へ進む」押下時のコールバック。skipNext は「次回から表示しない」のチェック状態。
   * 省略時はチェックボックス・ボタンを表示しない（カメラ画面の「読み取り方」から開く場合）
   */
  onConfirm?: (skipNext: boolean) => void
  /** 「もどる」または閉じるボタン押下時のコールバック */
  onClose: () => void
}

/**
 * OCR 注意喚起ダイアログ
 * スポット詳細画面の上にオーバーレイ表示する。
 */
export const DialogOcrWarning = memo(({ opened, ocrWarning, onConfirm, onClose }: Props) => {
  const { t } = useTranslation()
  const [skipNext, setSkipNext] = useState(false)
  const [prevOpened, setPrevOpened] = useState(opened)
  const dialogTitle = ocrWarning?.title ?? t('ocr.warning.title', 'レシート読み取りの注意点')

  // 開くたびに「次回から表示しない」を未チェックから始める（#3695）。
  // 閉じる時に戻すと閉じるアニメーション中にチェックが外れて見え、Modal の中身のアンマウントに任せると
  // アニメーション中に開き直した場合に残るため、opened が true に変わった時点で戻す。
  // localStorage に保存できない環境では「次へ進む」後も再表示されるので、そのときチェックが残らないようにする。
  // props の変化に合わせた state の調整は Effect ではなくレンダー中に行う（React 公式の推奨パターン）
  if (opened !== prevOpened) {
    setPrevOpened(opened)
    if (opened) setSkipNext(false)
  }

  return (
    <Dialog
      selectorState={opened}
      title={dialogTitle}
      showCloseButton={true}
      lockScroll={true}
      closeAction={onClose}
      closeOnClickOutside={false}
    >
      <div className="flex flex-col gap-6">
        <OcrWarningContent ocrWarning={ocrWarning} />
        {onConfirm != null && (
          <div className="flex flex-col gap-4">
            <Checkbox
              checked={skipNext}
              onChange={setSkipNext}
              label={t('common.popup.hide', '次回から表示しない')}
              labelClassName="text-[15px] text-black"
            />
            <div className="flex gap-3">
              <Button
                label={t('ocr.warning.back', 'もどる')}
                variant="system-back"
                size="md"
                fullWidth
                onClick={onClose}
              />
              <Button
                label={t('ocr.warning.confirm', '次へ進む')}
                variant="primary"
                size="md"
                fullWidth
                onClick={() => onConfirm(skipNext)}
              />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
})

DialogOcrWarning.displayName = 'DialogOcrWarning'
